import React from 'react'
import { getSizedImageUrl } from '../../../../../scripts/utils/image';
import { ISearchAndFilterProduct } from '../../../../context/search/types'
import ProductCardBox from '../../../svgs/ProductCardBox'
import safeJSONParse from '../../../../../scripts/utils/safeJsonParse';
import { formatMoney } from '../../../../../scripts/utils/currency';
import { TitleLines } from '../../../svgs/TitleLines'
import {TitleLinesInverse} from '../../../svgs/TitleLinesInverse'
import countRoast from '../../../../../scripts/utils/countRoast';
import getTasteStrFromTag from '../../../../../scripts/utils/getTasteStrFromTag';
import extractProductTags from '../../../../../scripts/utils/extractProductTags';
import RoastBeans from '../../../RoastBeans';

interface ISearchResultsProductCardComponent {
  product: ISearchAndFilterProduct,
  cardLangStrings: DOMStringMap
}

const SearchProductCard: React.FC<ISearchResultsProductCardComponent> = ({ product, cardLangStrings}): JSX.Element => {
  const { fromText, colourSwatches, roastText, tastesText, quickAddText, quickAddPlus, learnMoreText } = cardLangStrings

  const getProductImageUrl = (product: ISearchAndFilterProduct): string | undefined => {
    // Primary: use images array from ISearchAndFilterProduct type
    if (product?.images?.[0]?.src) {
      return product.images[0].src;
    }
    
    // Fallback: check other common Shopify shapes
    const p = product as any;
    if (typeof p?.featured_image === 'string') {
      return p.featured_image;
    }

    if (typeof p?.image === 'string') {
      return p.image;
    }

    return (
      p?.featured_image?.src ||
      p?.featured_image?.url ||
      p?.featuredImage?.url ||
      p?.featuredImage?.src ||
      p?.image?.src ||
      p?.image?.url
    );
  };

  const assemblePrice = (product: ISearchAndFilterProduct) => {
    const priceVaries: boolean = product.price.priceMax - product.price.priceMin > 0;

    const prodPrice = product.price.priceMin
    const pricePrependiture = (priceVaries ? fromText + ' ' : '');
    const priceFormatted = formatMoney(prodPrice, window['theme'].moneyFormat);

    return <p className="body-bold">{pricePrependiture} <span className="body-bold">{priceFormatted}</span></p>
  }

  const swatchHandler = (): Record<string, string> => {
    const swatches = extractProductTags(product, 'swatch')
    let style = {}
    if (swatches.length) {
      swatches.forEach(tag => {
        const tagColour = tag.split('_')[1] ? tag.split('_')[1].trim() : tag.split('-')[1].trim();
        const colour = safeJSONParse(colourSwatches)[tagColour];
        style = { '--swatch-color-default': colour  }
      })
    } else {
      style = { '--swatch-color-default': safeJSONParse(colourSwatches)['default']  }
    }
    return style
  }

  const roastCount: number = countRoast(product);
  const tasteStr: string = getTasteStrFromTag(product);

  const rawImageUrl = getProductImageUrl(product);
  // Use raw URL if it's already sized or if getSizedImageUrl fails
  const sizedImageUrl = (() => {
    if (!rawImageUrl) return '';
    // If URL already has sizing, use as-is
    if (/_\d+x\d+/.test(rawImageUrl)) {
      // Ensure protocol
      return rawImageUrl.startsWith('//') ? `https:${rawImageUrl}` : rawImageUrl;
    }
    // Try to size, fallback to raw URL
    const sized = getSizedImageUrl(rawImageUrl, '600x600_crop_center');
    const url = sized || rawImageUrl;
    // Ensure protocol (getSizedImageUrl strips it)
    return url.startsWith('//') ? `https:${url}` : url;
  })();

  return (
    <div className="cl-product-card glide__slide">
      <a href={product.url} title={product.name}>
        {/* Image */}
        <div className="cl-product-card-image relative">
          {sizedImageUrl ? (
            <img
              className="cl-product-card-image-img"
              src={product?.images?.[0]?.src}
              alt={product.name}
            />
          ) : null}

          <div className="hover-overlay flex justify-center align-center relative">
            <div className="hover-overlay-inner flex flex-column align-center">
              <TitleLines />
              <p>{cardLangStrings.hoverText}</p>
              <TitleLinesInverse />
            </div>
            <div className="svg-product-card-box">

            </div>
          </div>
          <ProductCardBox/>
        </div>
      </a>
      {/* Description */}
      <div className="cl-product-card-info flex-one">
        <div className="cl-product-card-info-top flex flex-column flex-one">
          <h2 className="cl-product-card-name heading-5 flex-one"><a href={product.url}>{product.name}</a></h2>
          {roastCount > 0 || tasteStr ? <div className="meta-info flex-one">
            {/* roast before taste, matching the server-rendered card (Liquid
              renders rows in tag order and Roast sorts before Taste) */}
            {roastCount > 0 ? <div className="tastes-like body-small flex align-center gap-small">{roastText} <span className="body-bold-small roast-meta flex align-center gap-xsmall">
              <RoastBeans roastCount={roastCount} />
            </span></div> : null}
            {tasteStr ? <div className="tastes-like body-small">{tastesText} <span className="body-bold-small taste-meta">{tasteStr}</span></div> : null}
          </div> : null}
          <div className="cl-product-card-price flex align-center space-between margin-t-auto">
            <div className="product-price-snippet">
              {assemblePrice(product)}
            </div>
            <div style={swatchHandler()}>
              {product.name === 'Gift Subscription' ?
                <a className="quick-add-btn swatch-btn" href={product.url}>{learnMoreText}<span className="quick-add-plus">{quickAddPlus}</span>
                </a> :
                <button data-quick-add-btn={product.handle} className="quick-add-btn swatch-btn" onClick={() => window['blubolt'].quickadd.open(product.handle, {swatch: swatchHandler()})}>
                  {quickAddText}<span className="quick-add-plus">{quickAddPlus}</span>
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchProductCard